import React from "react";
import { Badge, Tooltip } from "@mantine/core";
import { CalculationMethod, CommissionRecord } from "../../../types/commission";
import { Employee } from "../../../types/salon";
import {
  formatBucketTierRates,
  formatCalculationMethod,
  formatCommissionAmount,
  formatTierSummary,
} from "../../../utils/commissionUtils";

/**
 * Display helpers shared by the Commission Rates views: the legacy expandable
 * tab (CommissionRatesTab) and the flag-gated table + drawer
 * (CommissionRatesTable / CommissionRateDrawer).
 */

export const methodColor = (method: CalculationMethod): string => {
  switch (method) {
    case "STANDARD":
      return "var(--brand-teal)";
    case "COMMISSION_ONLY":
      return "var(--category-neutral)";
    case "GREATER_THAN":
      return "var(--brand-lime-green)";
    case "NUM_SERVICES":
      return "var(--brand-burnt-orange)";
    case "TIERED":
      return "var(--category-alt)";
    default:
      return "gray";
  }
};

/** True when a record pays specific services at their own rates. */
export const isServiceSpecific = (
  commission: CommissionRecord | null | undefined,
): boolean => (commission?.service_overrides?.length ?? 0) > 0;

/** The Service/Product text for a commission record. A TIERED record shows that
 * bucket's rate progression (e.g. "10% → 20%"); every other method shows its
 * flat rate. Returns null when that bucket has nothing configured. */
export const bucketDisplay = (
  commission: CommissionRecord | null | undefined,
  bucket: "services" | "products",
): string | null => {
  if (!commission) return null;
  if (commission.calculation_method === "TIERED") {
    return formatBucketTierRates(commission.tiers, bucket);
  }
  return bucket === "services"
    ? formatCommissionAmount(
        commission.service_amount,
        commission.service_amount_type,
      )
    : formatCommissionAmount(
        commission.product_amount,
        commission.product_amount_type,
      );
};

/** Method badge. For a TIERED commission the badge carries a tooltip with the
 * full per-bucket schedule. */
export const MethodBadge: React.FC<{ commission: CommissionRecord }> = ({
  commission,
}) => {
  const badge = (
    <Badge
      size="sm"
      variant="light"
      color={methodColor(commission.calculation_method)}
      style={{ whiteSpace: "nowrap", textAlign: "center" }}
    >
      {formatCalculationMethod(commission.calculation_method)}
    </Badge>
  );
  if (commission.calculation_method !== "TIERED") return badge;
  return (
    <Tooltip label={formatTierSummary(commission.tiers)} withArrow multiline>
      {badge}
    </Tooltip>
  );
};

const DATE_FORMAT: Intl.DateTimeFormatOptions = {
  month: "short",
  day: "numeric",
  year: "numeric",
};

/** "Nov 3, 2026" for a YYYY-MM-DD date (local, no timezone shift). */
export const formatEffectiveDate = (isoDate: string): string =>
  new Date(`${isoDate}T00:00:00`).toLocaleDateString("en-US", DATE_FORMAT);

const dayBefore = (isoDate: string): string => {
  const d = new Date(`${isoDate}T00:00:00`);
  d.setDate(d.getDate() - 1);
  return d.toLocaleDateString("en-US", DATE_FORMAT);
};

/** "Jan 1, 2026 – Feb 28, 2026": from a record's start to the day before the
 * next record's start (or "present" when it's the latest). */
export const formatEffectiveRange = (
  record: CommissionRecord,
  next: CommissionRecord | null | undefined,
): string => {
  const end = next ? dayBefore(next.effective_start_date) : "present";
  return `${formatEffectiveDate(record.effective_start_date)} – ${end}`;
};

/** "Ana Lee". */
export const staffName = (employee: Employee): string =>
  `${employee.first_name ?? ""} ${employee.last_name ?? ""}`.trim();
